/* eslint-disable max-classes-per-file */
/* eslint-disable @typescript-eslint/naming-convention */
export enum DeviceActionName {
	DeviceNameChanged = 'device_name_changed',
	Identified = 'identified'
}

export interface AnalyticsEvent {
	event: string;
	properties: object;
}

export class AppExitAnalyticsEvent implements AnalyticsEvent {
	public readonly event = 'application_exited';
	public readonly properties: {
		screen_resolution: string;
	};
	constructor(resolution: string) {
		this.properties = { screen_resolution: resolution };
	}
}

export class AppLaunchAnalyticsEvent implements AnalyticsEvent {
	public readonly event = 'application_launched';
	public readonly properties: {
		screen_resolution: string;
	};
	constructor(resolution: string) {
		this.properties = { screen_resolution: resolution };
	}
}

export class DeviceAvailableAnalyticsEvent implements AnalyticsEvent {
	public readonly event = 'device_available';
	public readonly properties: {
		firmware_version: string;
		hardware_id: string;
		interface_id: string;
		model: string;
	};
	constructor(firmwareVersion: string, hardwareId: string, interfaceId: string, model: string) {
		this.properties = {
			firmware_version: firmwareVersion,
			hardware_id: hardwareId,
			interface_id: interfaceId,
			model: model
		};
	}
}

export class DeviceOfflineAnalyticsEvent implements AnalyticsEvent {
	public readonly event = 'device_offline';
	public readonly properties: {
		hardware_id: string;
		model: string;
	};
	constructor(hardwareId: string, model: string) {
		this.properties = {
			hardware_id: hardwareId,
			model: model
		};
	}
}

export class DeviceActionAnalyticsEvent implements AnalyticsEvent {
	public readonly event = 'online_device_action';
	public readonly properties: {
		hardware_id: string;
		model: string;
		device_type: string;
		action: string;
	};
	constructor(hardwareId: string, model: string, deviceType: string, action: string) {
		this.properties = {
			hardware_id: hardwareId,
			model: model,
			device_type: deviceType,
			action: action
		};
	}
}

export class FirmwareUpdateCompletedAnalyticsEvent implements AnalyticsEvent {
	public readonly event = 'firmware_update_completed';
	public readonly properties: {
		hardware_id: string;
		model: string;
		new_version: string;
		old_version: string;
	};
	constructor(hardwareId: string, model: string, newFirmware: string, oldFirmware: string) {
		this.properties = {
			hardware_id: hardwareId,
			model: model,
			new_version: newFirmware,
			old_version: oldFirmware
		};
	}
}

export class FirmwareUpdateFailedAnalyticsEvent implements AnalyticsEvent {
	public readonly event = 'firmware_update_failed';
	public readonly properties: {
		failure_reason: string;
		hardware_id: string;
		model: string;
		new_version: string;
		old_version: string;
	};
	constructor(failureReason: string, hardwareId: string, model: string, newFirmware: string, oldFirmware: string) {
		this.properties = {
			failure_reason: failureReason,
			hardware_id: hardwareId,
			model: model,
			new_version: newFirmware,
			old_version: oldFirmware
		};
	}
}

export class FirmwareUpdateStartedAnalyticsEvent implements AnalyticsEvent {
	public readonly event = 'firmware_update_started';
	public readonly properties: {
		hardware_id: string;
		model: string;
		new_version: string;
		old_version: string;
	};
	constructor(hardwareId: string, model: string, newFirmware: string, oldFirmware: string) {
		this.properties = {
			hardware_id: hardwareId,
			model: model,
			new_version: newFirmware,
			old_version: oldFirmware
		};
	}
}

export class HelpTopicOpenedAnalyticsEvent implements AnalyticsEvent {
	public readonly event = 'help_topic_opened';
	public readonly properties: {
		topic_name: string;
	};
	constructor(topicName: string) {
		this.properties = { topic_name: topicName };
	}
}

export class AnalyticsOptedAnalyticsEvent implements AnalyticsEvent {
	public readonly event = 'opt_in_state';
	public readonly properties: {
		opted_in: boolean;
	};
	constructor(isAnalyticsOpted: boolean) {
		this.properties = { opted_in: isAnalyticsOpted };
	}
}

export class PreferenceChangedAnalyticsEvent implements AnalyticsEvent {
	public readonly event = 'preference_changed';
	public readonly properties: {
		new_value: string;
		old_value: string;
		preference_name: string;
	};
	constructor(newValue: string, oldValue: string, preferenceName: string) {
		this.properties = {
			new_value: newValue,
			old_value: oldValue,
			preference_name: preferenceName
		};
	}
}

export class CrashReportOptedAnalyticsEvent implements AnalyticsEvent {
	public readonly event = 'crash_report_opt_in_state';
	public readonly properties: {
		opted_in: boolean;
	};
	constructor(crashReportOpted: boolean) {
		this.properties = { opted_in: crashReportOpted };
	}
}

/**
 * Event to track page views within the application. Typically, this event will be sent every time a route
 * changes. The page_name property will typically correspond to the URL of the route.
 */
export class PageViewedEvent implements AnalyticsEvent {
	public readonly event = 'page_viewed';
	public readonly properties: {
		page_name: string;
	};

	constructor(pageName: string) {
		this.properties = { page_name: pageName };
	}
}

/**
 * Event that should be sent when a user signs in. This event should be augmented by the analytics
 * provider to add additional user-specific properties, such as the user and tenant IDs.
 */
export class SignInEvent implements AnalyticsEvent {
	public readonly event = 'sign_in';
	public readonly properties = {};
}

/**
 * Event that should be sent when a user signs out. This event will typically be sent only when a user
 * manually signs out, not when the application automatically signs a user out.
 */
export class SignOutEvent implements AnalyticsEvent {
	public readonly event = 'sign_out';
	public readonly properties = {};
}
